import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Ticket, Plus, Clock, AlertTriangle, CheckCircle, Circle, ChevronDown, ChevronUp } from "lucide-react";
import { backendApi } from "./api/backendApi";

const T = {
  green: '#1A3C2B', greenDeep: '#0E2118', greenTint: '#EDF3EF', greenGlow: 'rgba(26,60,43,0.1)',
  bg: '#F4F5F0', card: '#FFFFFF', line: '#E3E2DC', lineSoft: '#EEEEE9',
  ink: '#111418', body: '#3A4149', muted: '#687079', faint: '#9BA3AB',
};

const STATUS = {
  Open: { bg: '#EAF1FB', fg: '#3B6FA6', icon: Circle },
  "In Progress": { bg: '#FBF1E1', fg: '#B9822E', icon: AlertTriangle },
  Resolved: { bg: '#E8F3EC', fg: '#2F7D4F', icon: CheckCircle },
  Closed: { bg: '#F3F2ED', fg: '#6B7280', icon: CheckCircle },
};

const PRIORITY = {
  High: { bg: '#FCEAEA', fg: '#C0413D', dot: '#C0413D' },
  Medium: { bg: '#FBF1E1', fg: '#B9822E', dot: '#B9822E' },
  Low: { bg: '#F3F2ED', fg: '#6B7280', dot: '#9BA3AB' },
};

const fmtDate = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" }); } catch { return d; } };

const TicketCard = ({ ticket }) => {
  const [expanded, setExpanded] = useState(false);
  const st = STATUS[ticket.status] || STATUS.Closed;
  const pr = PRIORITY[ticket.priority] || PRIORITY.Low;
  const StIcon = st.icon;

  return (
    <div style={{
      background: T.card, border: `1.5px solid ${T.line}`, borderRadius: 16,
      overflow: 'hidden', transition: 'border-color 0.15s, box-shadow 0.15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = `${T.green}40`; e.currentTarget.style.boxShadow = '0 4px 16px rgba(26,60,43,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = T.line; e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Priority stripe */}
      <div style={{ height: 3, background: pr.dot }} />

      <div style={{ padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          {/* Ticket icon */}
          <div style={{
            width: 40, height: 40, borderRadius: 11, flexShrink: 0,
            background: st.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <StIcon size={18} color={st.fg} strokeWidth={2} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: T.ink, margin: 0, lineHeight: 1.3 }}>{ticket.title}</h4>
              <button onClick={() => setExpanded(!expanded)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.faint, padding: 2, flexShrink: 0 }}>
                {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: st.bg, color: st.fg }}>
                {ticket.status || 'Open'}
              </span>
              <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: pr.bg, color: pr.fg }}>
                {ticket.priority || 'Medium'} Priority
              </span>
              {ticket.category && (
                <span style={{ fontSize: 11.5, color: T.faint }}>{ticket.category}</span>
              )}
              <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: T.faint }}>
                <Clock size={11} /> {fmtDate(ticket.created_at)}
              </span>
            </div>

            {expanded && ticket.description && (
              <div style={{ marginTop: 12, padding: '12px 14px', background: T.lineSoft, borderRadius: 10 }}>
                <p style={{ fontSize: 13, color: T.body, margin: 0, lineHeight: 1.65 }}>{ticket.description}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function MyTickets({ onRaiseTicket }) {
  const storedUser = backendApi.auth.getStoredUser();
  const [filter, setFilter] = useState("All");

  const { data: tickets = [], isLoading } = useQuery({
    queryKey: ["my-tickets", storedUser?.id],
    queryFn: () => backendApi.entities.Ticket.filter({ raised_by: storedUser?.id }),
    initialData: [],
  });

  const sorted = [...tickets].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  const filtered = filter === "All" ? sorted : sorted.filter(t => t.status === filter);

  const counts = tickets.reduce((acc, t) => { acc[t.status || 'Open'] = (acc[t.status || 'Open'] || 0) + 1; return acc; }, {});

  return (
    <div style={{ background: T.bg, minHeight: '100%', padding: 28 }}>
      <style>{`.mt-btn{transition:all 0.15s;cursor:pointer} .mt-btn:hover:not(:disabled){transform:translateY(-1px)}`}</style>

      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 26 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: T.ink, margin: 0, letterSpacing: '-0.02em' }}>My Tickets</h2>
          <p style={{ fontSize: 12.5, color: T.muted, margin: '3px 0 0' }}>Support tickets you've raised</p>
        </div>
        <button className="mt-btn" onClick={onRaiseTicket} style={{
          display: 'flex', alignItems: 'center', gap: 8, height: 40, padding: '0 20px',
          background: T.green, border: 'none', borderRadius: 12, fontSize: 13.5, fontWeight: 600,
          color: '#fff', cursor: 'pointer', boxShadow: '0 2px 12px rgba(26,60,43,0.2)',
        }}>
          <Plus size={15} strokeWidth={2.5} /> Raise Ticket
        </button>
      </div>

      {/* Status overview */}
      {tickets.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 12, marginBottom: 22 }}>
          {Object.entries(STATUS).map(([status, style]) => {
            const count = counts[status] || 0;
            const Icon = style.icon;
            return (
              <button key={status} onClick={() => setFilter(filter === status ? 'All' : status)}
                style={{
                  background: filter === status ? style.bg : T.card,
                  border: `1.5px solid ${filter === status ? style.fg + '40' : T.line}`,
                  borderRadius: 14, padding: '14px 16px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <Icon size={15} color={style.fg} strokeWidth={2} />
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: style.fg }}>{status}</span>
                </div>
                <p style={{ fontSize: 26, fontWeight: 800, color: T.ink, margin: 0, letterSpacing: '-0.02em' }}>{count}</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Ticket list */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ width: 36, height: 36, border: `3px solid ${T.greenTint}`, borderTopColor: T.green, borderRadius: '50%', margin: '0 auto 12px', animation: 'spin 0.8s linear infinite' }} />
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <p style={{ color: T.faint, fontSize: 13 }}>Loading tickets…</p>
        </div>
      ) : filtered.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(t => <TicketCard key={t.id} ticket={t} />)}
        </div>
      ) : (
        <div style={{ background: T.card, border: `1.5px solid ${T.line}`, borderRadius: 18, padding: '64px 20px', textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, background: T.greenTint, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Ticket size={28} color={T.green} strokeWidth={1.8} />
          </div>
          <p style={{ fontSize: 15, fontWeight: 700, color: T.ink, margin: '0 0 6px' }}>
            {tickets.length === 0 ? "No tickets yet" : `No ${filter} tickets`}
          </p>
          <p style={{ fontSize: 13, color: T.muted, margin: '0 0 20px' }}>
            {tickets.length === 0 ? "Raise a ticket if you need help from the support team." : `Try a different status filter.`}
          </p>
          {tickets.length === 0 && (
            <button className="mt-btn" onClick={onRaiseTicket}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: T.green, border: 'none', borderRadius: 10, fontSize: 13.5, fontWeight: 600, color: '#fff', cursor: 'pointer' }}>
              <Plus size={15} /> Raise Your First Ticket
            </button>
          )}
        </div>
      )}
    </div>
  );
}